import type {
  CatalogNode,
  CatalogNodeType,
} from '@risksmart-app/domain/src/types/catalog';
import type { IngestionFilters } from '@risksmart-app/domain/src/types/ingestion-filters';

/**
 * Pure selection ↔ filter logic for the regulatory-feed catalog picker.
 *
 * Source of truth in the UI is a Set of selected node externalIds. These helpers
 * convert between that set and the stored `IngestionFilters` document, and build
 * the tree the Cloudscape table renders. Keeping this logic pure (no React, no
 * Cloudscape) makes the round-trip independently testable.
 */

export interface CatalogTreeRow {
  externalId: string;
  title: string;
  type: CatalogNodeType;
  children: CatalogTreeRow[];
}

const groupBy = <T>(items: T[], key: (item: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const list = map.get(k);
    if (list) {
      list.push(item);
    } else {
      map.set(k, [item]);
    }
  }

  return map;
};

const chaptersByStandardId = (
  nodes: CatalogNode[]
): Map<string, CatalogNode[]> =>
  groupBy(
    nodes.filter((n) => n.type === 'chapter'),
    (c) => c.parentId ?? ''
  );

/** Build the nested regulator → standard → chapter tree the table renders. */
export const buildCatalogTree = (nodes: CatalogNode[]): CatalogTreeRow[] => {
  const standardsByRegulator = groupBy(
    nodes.filter((n) => n.type === 'standard'),
    (s) => s.parentId ?? ''
  );
  const chaptersByStandard = chaptersByStandardId(nodes);

  return nodes
    .filter((n) => n.type === 'regulator')
    .map((regulator) => ({
      externalId: regulator.externalId,
      title: regulator.title,
      type: 'regulator' as const,
      children: (standardsByRegulator.get(regulator.externalId) ?? []).map(
        (standard) => ({
          externalId: standard.externalId,
          title: standard.title,
          type: 'standard' as const,
          children: (chaptersByStandard.get(standard.externalId) ?? []).map(
            (chapter) => ({
              externalId: chapter.externalId,
              title: chapter.title,
              type: 'chapter' as const,
              children: [],
            })
          ),
        })
      ),
    }));
};

/**
 * Build the set of selected externalIds from stored filters. A fully-included
 * standard (in `standards.ids`) selects the standard AND all its current
 * chapters, so the table renders it fully-checked and any chapters added to the
 * catalog since the last save are picked up on the next save.
 */
export const hydrateSelectedIds = (
  filters: IngestionFilters | undefined,
  nodes: CatalogNode[]
): Set<string> => {
  const selected = new Set<string>();
  const chaptersByStandard = chaptersByStandardId(nodes);

  for (const standardId of filters?.standards?.ids ?? []) {
    selected.add(standardId);
    for (const chapter of chaptersByStandard.get(standardId) ?? []) {
      selected.add(chapter.externalId);
    }
  }

  for (const chapterId of filters?.chapters?.ids ?? []) {
    selected.add(chapterId);
  }

  return selected;
};

/**
 * Convert the selected-id set into the picker-owned slice of IngestionFilters.
 * A standard is fully-included (→ `standards.ids`, no chapter entries) when all
 * its catalog chapters are selected; otherwise its selected chapters go to
 * `chapters.ids`. Derived from chapter membership so it is robust to whatever
 * the table reports for parent rows.
 */
export const serializeSelection = (
  selectedIds: Set<string>,
  nodes: CatalogNode[]
): Pick<IngestionFilters, 'standards' | 'chapters'> => {
  const chaptersByStandard = chaptersByStandardId(nodes);
  const standardIds: string[] = [];
  const chapterIds: string[] = [];

  for (const standard of nodes.filter((n) => n.type === 'standard')) {
    const chapters = chaptersByStandard.get(standard.externalId) ?? [];

    if (chapters.length === 0) {
      if (selectedIds.has(standard.externalId)) {
        standardIds.push(standard.externalId);
      }
      continue;
    }

    const selectedChapters = chapters.filter((c) =>
      selectedIds.has(c.externalId)
    );

    if (selectedChapters.length === chapters.length) {
      standardIds.push(standard.externalId);
    } else if (selectedChapters.length > 0) {
      chapterIds.push(...selectedChapters.map((c) => c.externalId));
    }
  }

  return {
    standards: standardIds.length
      ? { mode: 'include', ids: standardIds }
      : undefined,
    chapters: chapterIds.length
      ? { mode: 'include', ids: chapterIds }
      : undefined,
  };
};

/**
 * Merge the picker's standards/chapters slice into the stored filters,
 * preserving any `rules` block (and any other keys) untouched.
 */
export const mergeIngestionFilters = (
  existing: IngestionFilters | undefined,
  picker: Pick<IngestionFilters, 'standards' | 'chapters'>
): IngestionFilters => {
  const merged: IngestionFilters = { ...existing };

  if (picker.standards) {
    merged.standards = picker.standards;
  } else {
    delete merged.standards;
  }

  if (picker.chapters) {
    merged.chapters = picker.chapters;
  } else {
    delete merged.chapters;
  }

  return merged;
};

/** Saved ids (standards + chapters) no longer present in the latest catalog. */
export const findStaleIds = (
  filters: IngestionFilters | undefined,
  nodes: CatalogNode[]
): string[] => {
  const known = new Set(nodes.map((n) => n.externalId));
  const saved = [
    ...(filters?.standards?.ids ?? []),
    ...(filters?.chapters?.ids ?? []),
  ];

  return [...new Set(saved)].filter((id) => !known.has(id));
};

/** Count fully-included standards and individually-selected chapters. */
export const countSelection = (
  selectedIds: Set<string>,
  nodes: CatalogNode[]
): { standards: number; chapters: number } => {
  const picker = serializeSelection(selectedIds, nodes);

  return {
    standards: picker.standards?.ids.length ?? 0,
    chapters: picker.chapters?.ids.length ?? 0,
  };
};

/** Filter the tree by title (case-insensitive), keeping ancestors of matches. */
export const filterCatalogTree = (
  tree: CatalogTreeRow[],
  query: string
): CatalogTreeRow[] => {
  const q = query.trim().toLowerCase();
  if (!q) {
    return tree;
  }

  const filterRows = (rows: CatalogTreeRow[]): CatalogTreeRow[] =>
    rows.flatMap((row) => {
      const children = filterRows(row.children);
      if (row.title.toLowerCase().includes(q) || children.length > 0) {
        return [{ ...row, children }];
      }

      return [];
    });

  return filterRows(tree);
};

/** Flatten a (possibly filtered) tree to the rows that should be auto-expanded. */
export const expandableRowIds = (tree: CatalogTreeRow[]): string[] => {
  const ids: string[] = [];
  const walk = (rows: CatalogTreeRow[]) => {
    for (const row of rows) {
      if (row.children.length > 0) {
        ids.push(row.externalId);
        walk(row.children);
      }
    }
  };
  walk(tree);

  return ids;
};
